<template>
  <FormSection>
    <Card>
      <ElForm
        ref="accountInformation"
        autocomplete="off"
        :model="accountInformationModel"
        :rules="rules"
        label-position="top"
        @submit.native.prevent="submitAccountInformationForm"
      >
        <ElFormItem :label="$t('EMAIL')" prop="email">
          <ElInput
            :value="email"
            clearable
            :placeholder="$t('EMAIL')"
            type="email"
            dir="ltr"
            name="email"
            class="text-latin"
            disabled
          />
          <FieldError :errors="errors" field="email" />
          <p class="el-form-item__help el-form-item__help--warning ">
            <i class="el-icon-warning" />
            {{ $t('AUTH.STEPS.LEGAL_EMAIL_WARNING') }}
          </p>
        </ElFormItem>

        <ElFormItem
          :label="$t('PUBLISHER_ACCOUNT.COMPANY_FULL_NAME')"
          prop="name"
        >
          <ElInput
            v-model="accountInformationModel.name"
            dir="rtl"
            name="name"
          />
          <FieldError :errors="errors" field="name" />
          <p class="el-form-item__help">
            <i class="el-icon-warning" />
            {{ $t('AUTH.STEPS.NAME_WARNING') }}
          </p>
        </ElFormItem>

        <ElFormItem
          :label="$t('PUBLISHER_ACCOUNT.COMPANY_NATIONAL_ID')"
          prop="national_id"
        >
          <ElInput
            v-model="accountInformationModel.national_id"
            name="national_id"
            :maxlength="11"
            @input.native="verifyNationalId"
          />
          <FieldError :errors="errors" field="national_id" />
          <p
            v-if="nationalIdIsUsed"
            class="el-form-item__help el-form-item__help--warning"
          >
            <i class="el-icon-warning" />
            {{ $t('PUBLISHER_ACCOUNT.COMPANY_NATIONAL_ID_USED_WARNING') }}
          </p>
        </ElFormItem>

        <ElFormItem :label="$t('MOBILE_PHONE')" prop="phone">
          <ElInput
            v-model="accountInformationModel.phone"
            type="tel"
            dir="ltr"
            name="phone"
            placeholder="09121234567"
            :maxlength="11"
          />

          <FieldError :errors="errors" field="phone" />
          <p class="el-form-item__help">
            <i class="el-icon-warning" />
            {{ $t('AUTH.STEPS.CELLPHONE_WARNING') }}
          </p>
        </ElFormItem>

        <ElFormItem :label="$t('POSTAL_CODE')" prop="postal_code">
          <FieldError :errors="errors" field="postal_code" />
          <ElInput
            v-model="accountInformationModel.postal_code"
            dir="ltr"
            name="postal_code"
            :maxlength="10"
          />
        </ElFormItem>

        <ElFormItem :label="$t('PROVINCE')" prop="province">
          <ElSelect
            v-model="accountInformationModel.province"
            class="show-for-medium"
            name="province"
            :placeholder="$t('CHOOSE')"
          >
            <ElOption
              v-for="province in provinces"
              :key="province.id"
              :label="getProvinceLabel(province)"
              :value="province.slug"
            />
          </ElSelect>

          <select
            v-model="accountInformationModel.province"
            class="hide-for-medium"
            name="province"
          >
            <option
              v-for="province in provinces"
              :key="province.id"
              :value="province.slug"
            >
              {{ getProvinceLabel(province) }}
            </option>
          </select>
          <FieldError :errors="errors" field="province" />
        </ElFormItem>

        <ElFormItem :label="$t('CITY')" prop="city" required>
          <ElInput v-model="accountInformationModel.city" name="city" />
          <FieldError :errors="errors" field="city" />
        </ElFormItem>

        <ElFormItem :label="$t('ADDRESS')" prop="address">
          <ElInput
            v-model="accountInformationModel.address"
            :autosize="{ minRows: 2, maxRows: 4 }"
            name="address"
            :maxlength="150"
            type="textarea"
          />
          <FieldError :errors="errors" field="address" />
        </ElFormItem>

        <ElFormItem
          :label="$t('PUBLISHER_ACCOUNT.REGISTRATION_NUMBER')"
          prop="registration_number"
        >
          <ElInput
            v-model="accountInformationModel.registration_number"
            name="registration_number"
            :maxlength="6"
          />
          <FieldError :errors="errors" field="registration_number" />
        </ElFormItem>

        <ElFormItem
          :label="$t('PUBLISHER_ACCOUNT.ECONOMIC_CODE')"
          prop="economic_code"
        >
          <ElInput
            v-model="accountInformationModel.economic_code"
            name="economic_code"
          />
          <FieldError :errors="errors" field="economic_code" />
        </ElFormItem>

        <ElFormItem
          :label="$t('PUBLISHER_ACCOUNT.CEO_NATIONAL_CARD')"
          prop="ceo_national_card"
        >
          <Droppable
            :has-preview="true"
            :has-progress="true"
            file-types="image/*"
            :upload-url="uploadPaths.nationalCard"
            @uploaded="uploadCeoNationalImage"
          >
            <div slot="label">
              {{ $t('PUBLISHER_ACCOUNT.DRAG_CEO_NATIONAL_IMAGE') }}
            </div>
          </Droppable>

          <FieldError :errors="errors" field="ceo_national_card" />
          <p class="el-form-item__help">
            <i class="el-icon-warning" />
            {{ $t('AUTH.STEPS.CEO_IMAGE_WARNING') }}
          </p>
        </ElFormItem>

        <div class="auth-steps__navigation">
          <ElButton type="default" @click.native="handlePrevPage">
            {{ $t('AUTH.STEPS.PREV_LEVEL_BTN') }}
          </ElButton>

          <ElButton type="primary" native-type="submit" :loading="loading">
            {{ $t('AUTH.STEPS.NEXT_LEVEL_BTN') }}
          </ElButton>
        </div>

        <ErrorsList v-if="errors" :errors="errors" />
      </ElForm>
    </Card>
  </FormSection>
</template>

<script>
import debounce from 'lodash.debounce';
import normalizeNumbers from '@/utils/normalizeNumbers';
import form from '@/mixins/form';
import lang from '@/mixins/lang';
import provinces from '@/utils/provinces';
import { Publishers, Register } from '@/api/api';
import Droppable from '@@/layout/Droppable';
import ErrorsList from '@@/common/ErrorsList';
import FieldError from '@@/common/FieldError';
import FormSection from '@@/common/layout/FormSection';
import Card from '@@/common/Card';

export default {
  name: 'DomesticLegal',

  components: {
    FieldError,
    ErrorsList,
    Droppable,
    FormSection,
    Card,
  },

  mixins: [form, lang],

  props: {
    email: {
      type: String,
      required: true,
    },
    errors: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      nationalIdIsUsed: false,
      accountInformationModel: {
        name: null,
        ceo_national_card: {
          id: null,
          url: null,
        },
        national_id: null,
        economic_code: null,
        province: null,
        city: null,
        address: null,
        phone: null,
        postal_code: null,
      },

      uploadPaths: Publishers.uploadPaths,

      provinces,
    };
  },

  computed: {
    rules() {
      const rules = {
        required: true,
        message: this.$t('VALIDATION_REQUIRED_ERR'),
        trigger: ['blur', 'change'],
      };

      return {
        name: {
          ...rules,
          type: 'string',
        },
        phone: [
          rules,
          {
            pattern: /^09\d{9}$/i,
            message: this.$t('PUBLISHER_ACCOUNT.INVALID_PHONE'),
          },
        ],
        postal_code: [
          rules,
          {
            pattern: /^\d{10}$/i,
            message: this.$t('PUBLISHER_ACCOUNT.INVALID_POSTAL_CODE'),
            trigger: 'blur',
          },
        ],
        province: rules,
        city: {
          ...rules,
          type: 'string',
        },
        address: {
          ...rules,
          type: 'string',
        },
        registration_number: rules,
        economic_code: rules,
        national_id: [
          rules,
          {
            pattern: /^\d{11}$/i,
            message: this.$t('PUBLISHER_ACCOUNT.INVALID_COMPANY_NATIONAL_ID'),
            trigger: 'blur',
          },
        ],
        ceo_national_card: rules,
      };
    },
  },

  watch: {
    accountInformationModel: {
      deep: true,
      handler: normalizeNumbers,
    },
  },

  methods: {
    verifyNationalId() {
      this.nationalIdIsUsed = false;
      if (!this.verifyByApiCall) {
        this.verifyByApiCall = debounce(() => {
          const { national_id: nid } = this.accountInformationModel;
          if (nid?.length < 11) return;

          // since length becomes 10 only once, we don't need to throttle this call
          return Register.verifyNationalCodeOrId({ national_id: nid })
            .then(({ body: { is_valid: isValid } }) => {
              this.nationalIdIsUsed = !isValid;
            })
            .catch(() => {
              /* DO Nothing, just to prevent UnhandledRejection error */
            });
        }, 1000);
      }
      this.verifyByApiCall();
    },

    submitAccountInformationForm() {
      if (!this.isValid('accountInformation')) {
        return false;
      }
      this.$emit('submitAccountInformation', this.accountInformationModel);
    },

    handlePrevPage() {
      return this.$emit('prevStep');
    },

    getProvinceLabel(province) {
      if (this.lang !== 'fa') {
        return province.slug;
      }
      return province.title;
    },

    uploadCeoNationalImage(src) {
      this.accountInformationModel.ceo_national_card.id = src;
    },
  },
};
</script>
